import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import {
  LegalDocumentVersion, LegalDocumentContent, ConsentStats,
  DEFAULT_TOS_SECTIONS, DEFAULT_PP_SECTIONS,
} from './legalDefaults';

export function useLegalEditor() {
  const [activeDoc, setActiveDoc] = useState<'terms_of_service' | 'privacy_policy'>('terms_of_service');
  const [versions, setVersions] = useState<LegalDocumentVersion[]>([]);
  const [content, setContent] = useState<LegalDocumentContent | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState<LegalDocumentContent | null>(null);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishVersion, setPublishVersion] = useState('');
  const [publishSummary, setPublishSummary] = useState('');
  const [publishRequiresReconsent, setPublishRequiresReconsent] = useState(false);
  const [consentStats, setConsentStats] = useState<ConsentStats | null>(null);
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  const showNotification = useCallback((message: string, type: 'success' | 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), type === 'error' ? 30000 : 5000);
  }, []);

  const fetchContent = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('admin_get_site_content', {
        p_key: `legal_${activeDoc}`
      });
      if (error) throw error;

      if (data && typeof data === 'object' && Object.keys(data).length > 0 && (data as any).sections) {
        const parsed = typeof data === 'string' ? JSON.parse(data) : data;
        const validated = { ...parsed, sections: parsed.sections || [] };
        setContent(validated);
        setEditingContent(JSON.parse(JSON.stringify(validated)));
      } else {
        // No content yet — initialize with defaults
        const defaultContent: LegalDocumentContent = {
          document_type: activeDoc,
          version: '1.0',
          sections: activeDoc === 'terms_of_service' ? DEFAULT_TOS_SECTIONS : DEFAULT_PP_SECTIONS,
          last_updated: new Date().toISOString().split('T')[0],
          effective_date: new Date().toISOString().split('T')[0],
          entity_name: 'Paulo Marin, doing business as RidePulse',
          entity_type: 'individual',
          min_age: 16,
        };
        setContent(defaultContent);
        setEditingContent(JSON.parse(JSON.stringify(defaultContent)));
      }

      // Fetch version history
      const { data: versionData } = await supabase.rpc('admin_get_legal_versions', {
        p_document_type: activeDoc
      });
      if (versionData) setVersions(versionData);

      // Fetch consent stats
      const { data: statsData } = await supabase.rpc('admin_get_legal_consent_stats', {
        p_document_type: activeDoc
      });
      if (statsData) {
        const stats = Array.isArray(statsData) ? statsData[0] : statsData;
        setConsentStats(stats);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      showNotification(`Failed to load content: ${message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [activeDoc, showNotification]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const handleSave = async () => {
    if (!editingContent) return;
    setSaving(true);
    try {
      const { error } = await supabase.rpc('admin_set_site_content', {
        p_key: `legal_${activeDoc}`,
        p_value: editingContent
      });
      if (error) throw error;
      setContent(JSON.parse(JSON.stringify(editingContent)));
      showNotification('Draft saved successfully', 'success');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      showNotification(`Failed to save: ${message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!editingContent || !publishVersion) return;
    setPublishing(true);
    try {
      // Save content first
      const updatedContent = {
        ...editingContent,
        version: publishVersion,
        last_updated: new Date().toISOString().split('T')[0],
      };
      const { error: saveError } = await supabase.rpc('admin_set_site_content', {
        p_key: `legal_${activeDoc}`,
        p_value: updatedContent
      });
      if (saveError) throw saveError;

      // Create new version record
      const { error: versionError } = await supabase.rpc('admin_publish_legal_version', {
        p_document_type: activeDoc,
        p_version: publishVersion,
        p_title: activeDoc === 'terms_of_service' ? 'Terms of Service' : 'Privacy Policy',
        p_summary: publishSummary || null,
        p_requires_reconsent: publishRequiresReconsent,
      });
      if (versionError) throw versionError;

      setContent(JSON.parse(JSON.stringify(updatedContent)));
      setEditingContent(JSON.parse(JSON.stringify(updatedContent)));
      setShowPublishModal(false);
      setPublishVersion('');
      setPublishSummary('');
      setPublishRequiresReconsent(false);
      showNotification(
        publishRequiresReconsent
          ? `Published v${publishVersion} — existing users will be prompted to re-accept`
          : `Published v${publishVersion} — minor update, no re-consent required`,
        'success'
      );
      await fetchContent();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      showNotification(`Failed to publish: ${message}`, 'error');
    } finally {
      setPublishing(false);
    }
  };

  const openPublishModal = () => {
    const currentVersion = editingContent?.version || '1.0';
    const parts = currentVersion.split('.');
    const minor = parseInt(parts[1] || '0') + 1;
    setPublishVersion(`${parts[0]}.${minor}`);
    setShowPublishModal(true);
  };

  const updateSection = (sectionId: string, field: 'title' | 'content', value: string) => {
    if (!editingContent) return;
    setEditingContent({
      ...editingContent,
      sections: editingContent.sections.map(s =>
        s.id === sectionId ? { ...s, [field]: value } : s
      ),
    });
  };

  const addSection = () => {
    if (!editingContent) return;
    const nextNum = editingContent.sections.length + 1;
    setEditingContent({
      ...editingContent,
      sections: [
        ...editingContent.sections,
        { id: String(nextNum), title: `${nextNum}. New Section`, content: '' },
      ],
    });
  };

  const removeSection = (sectionId: string) => {
    if (!editingContent) return;
    setEditingContent({
      ...editingContent,
      sections: editingContent.sections.filter(s => s.id !== sectionId),
    });
  };

  const moveSection = (sectionId: string, direction: 'up' | 'down') => {
    if (!editingContent) return;
    const idx = editingContent.sections.findIndex(s => s.id === sectionId);
    if (idx < 0) return;
    if (direction === 'up' && idx === 0) return;
    if (direction === 'down' && idx === editingContent.sections.length - 1) return;
    const newSections = [...editingContent.sections];
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    [newSections[idx], newSections[swapIdx]] = [newSections[swapIdx], newSections[idx]];
    setEditingContent({ ...editingContent, sections: newSections });
  };

  const hasUnsavedChanges = JSON.stringify(content) !== JSON.stringify(editingContent);

  return {
    activeDoc, setActiveDoc, versions, content, loading, saving, publishing,
    notification, setNotification, expandedSection, setExpandedSection,
    editingContent, setEditingContent, showPublishModal, setShowPublishModal,
    publishVersion, setPublishVersion, publishSummary, setPublishSummary,
    publishRequiresReconsent, setPublishRequiresReconsent, consentStats,
    showVersionHistory, setShowVersionHistory, hasUnsavedChanges,
    fetchContent, handleSave, handlePublish, openPublishModal,
    updateSection, addSection, removeSection, moveSection,
  };
}

export type LegalModel = ReturnType<typeof useLegalEditor>;
